const GET = 'GET';
const POST = 'POST';
const PUT = 'PUT';
const PATCH = 'PATCH';
const DELETE = 'DELETE';

export const acceptedContentTypes = {
  json: ['application/json'],
  text: ['text/plain', 'text/xml'],
  jpeg: ['image/jpeg', 'image/jpg'],
  png: ['image/png'],
};
export const defaultApiOptions = {
  credentials: 'same-origin',
  headers: {
    Accept: Object.keys(acceptedContentTypes).reduce((previousValue, nextValue) => previousValue.concat(acceptedContentTypes[nextValue]), []).join(','),
  },
};
/**
 * this is APIService class.
 */
export class APIService {
/**
 * @param  {String} baseUrl The base of the url that is used to make requests to eg. https://www.sports-tracker.com/apiservice/v1/
 * @param  {Object} options Options that are used in requests (Headers, credentials, auth...) see defaultApiOptions
 */
  constructor(baseUrl, options) {
    this.baseUrl = baseUrl;
    this.options = options || { ...defaultApiOptions, ...options };
  }

  /**
   * @param  {String} url           the url to make the request to
   * @param  {Object} params=null   parameters to be added to the get request
   * @return {Promise}
   */
  get(url, params = null) {
    if (url.indexOf('?') !== -1) return Promise.reject(new Error('Url parameters have to be given as the second parameter eg: {param1: \'value1\'}'));
    const qs = Object.entries(params || {})
      .map((e) => [
        encodeURIComponent(e[0]),
        Array.isArray(e[1]) ? e[1].map(encodeURIComponent).join(',') : encodeURIComponent(e[1]),
      ])
      .map((e) => e.join('='))
      .join('&');
    const urlAndQs = url + (qs ? `?${qs}` : '');
    return this.request(urlAndQs, { ...this.options, method: GET });
  }

  /**
   * @param  {String} url  the url to make the request to
   * @param  {Object} data data to be sent
   * @return  {Promise}
   */
  post(url, data) {
    if (!data) return Promise.reject(new Error('Data is required in POST'));
    return this.request(url, { ...this.options, method: POST, body: data });
  }

  /**
   * @param  {String} url  the url to make the request to
   * @param  {String} resourceName  name of the resource to update
   * @param  {String} resourceId    id of the resource to update
   * @param  {Object} data data to be sent
   * @return {Promise}
   */
  put(url, resourceName, resourceId, data) {
    if (!resourceName || !resourceId) return Promise.reject(new Error('ResourceName and ResourceId are required in PUT'));
    if (!data) return Promise.reject(new Error('Data is required in PUT'));
    return this.request(`${url}/${resourceName}/${resourceId}`, { ...this.options, method: PUT, body: data });
  }

  /**
   * @param  {String} url  the url to make the request to
   * @param  {String} resourceName  name of the resource to update
   * @param  {String} resourceId    id of the resource to update
   * @param  {Object} data data to be sent
   * @return {Promise}
   */
  patch(url, resourceName, resourceId, data) {
    if (!resourceName || !resourceId) return Promise.reject(new Error('ResourceName and ResourceId are required in PATCH'));
    if (!data) return Promise.reject(new Error('Data is required in PATCH'));
    return this.request(`${url}/${resourceName}/${resourceId}`, { ...this.options, method: PATCH, body: data });
  }

  /**
   * @param  {String} url           the url to make the request to
   * @param  {String} resourceName  name of the resource to delete
   * @param  {String} resourceId    id of the resource to delete
   * @return {Promise}
   */
  delete(url, resourceName, resourceId) {
    if (!resourceName || !resourceId) return Promise.reject(new Error('ResourceName and ResourceId are required in DELETE'));
    return this.request(`${url}/${resourceName}/${resourceId}`, { ...this.options, method: DELETE });
  }

  /**
   * @param  {Object} response an instance of an Response object
   * @param  {Object} options  the options that were used to make the request
   */
  static resolve(response, options) {
    if (response.status >= 200 && response.status < 400) {
      const contentType = response.headers.get('Content-Type') && response.headers.get('Content-Type').split(';')[0].trim();
      if (options.headers.Accept.split(',').some((acceptedContentType) => contentType === acceptedContentType)) {
        if (acceptedContentTypes.json.some((acceptedContentType) => acceptedContentType === contentType)) return response.json();
        if (acceptedContentTypes.text.some((acceptedContentType) => acceptedContentType === contentType)) return response.text();
        return Promise.resolve(response);
      }
      return Promise.reject(new Error(`Content type (${contentType}) not one of the accepted ones: ${JSON.stringify(acceptedContentTypes)}`));
    }
    const error = new Error(`Unexpected status: ${response.status}: ${response.statusText}`);
    error.response = response;
    return Promise.reject(error);
  }

  /**
   * @param  {String|Error} error either a string that will be used to create a new Error or a Error that will be thrown as is
   */
  static reject(error) {
    if (!(error instanceof Error)) throw new Error(error);
    throw error;
  }

  /**
   * @param  {String} authToken token that will be set into headers and used for subsequent requests
   */
  setAuthentication(authToken) {
    this.options = { ...this.options, headers: { ...this.options.headers, STTAuthorization: authToken } };
  }

  /**
   * @param  {String} url     the url to make the request to, gets appended to the baseUrl of the instance
   * @param  {Object} options options to bec used when making the request
   * @return {Promise}
   */
  request(url, options) {
    return fetch(`${this.baseUrl}${url}`, options).then((response) => APIService.resolve(response, options), APIService.reject);
  }
}

let baseUrl = 'http://localhost:3000/apiserver/v1/';
if (!__DEV__) {
  // baseUrl = `http${window.location.hostname === 'localhost' ? '' : 's'}://${window.location.host}/apiserver/v1/`;
  baseUrl = `//${window.location.host}/apiserver/v1/`;
}
if (__TEST__) {
  baseUrl = `http://${window.location.host}/apiserver/v1/`;
}
// Initialize an instance of the APIService class with a base url to the main REST-api and export it for ease of use
const apiService = new APIService(baseUrl, defaultApiOptions);
export default apiService;
